/**
 * QUI PEUT LIRE LE PLANNING — module PUR, et la SEULE réponse à cette question.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Le défaut qui a fait naître ce module (11/09/2026) ──────────────────────
 *
 * « L'affichage du planning par mois doit être disponible pour tout le
 *  personnel. » — Manel Haddada, qui porte `redacteur / verificateur /
 *  lecteur`, mais pas `rh`.
 *
 * La règle métier était pourtant déjà prise, et côté moteur : la section
 * `planning` porte `regle: 'lectureLarge'` (`backend/src/ged-sections.ts`) —
 * « savoir qui tient quel poste quel jour concerne toute l'équipe, c'est même
 * la raison d'être d'un planning affiché ». La route sert donc le mois à
 * quiconque a `planning.voir`.
 *
 * Mais l'INTERFACE reposait la question QUATRE fois, à la main, et deux des
 * quatre copies testaient encore le seul rôle `rh` :
 *
 *   · `Rh.tsx`              — correcte (elle avait reçu la distinction au lot N6)
 *   · `store.loadRh`        — correcte, et son commentaire explique pourquoi
 *   · `store.loadRhMois`    — ⚠️ FAUSSE : ne chargeait rien
 *   · `RhPlanningMois.tsx`  — ⚠️ FAUSSE : `return null`, page blanche
 *
 * Le résultat n'était pas un refus, c'était une AFFIRMATION FAUSSE : une grille
 * de mois vide se lit « personne n'est planifié ce mois-ci », jamais « je n'ai
 * pas eu le droit de demander ».
 *
 * ⚠️ NE PAS REFAIRE LA CONDITION SUR PLACE. C'est la duplication qui a produit
 * le défaut, pas la condition elle-même : elle est juste dans deux copies sur
 * quatre. La cinquième copie dériverait à son tour, et une grille vide ne
 * réveille aucun test.
 *
 * ── Pourquoi ici, et pourquoi pur ──────────────────────────────────────────
 *
 * Aucun import d'exécution : ce module s'éprouve hors navigateur
 * (`backend/tests/front-acces-planning.test.js`). Les droits d'affichage sont
 * exactement le genre de règle qu'on croit évidente et qu'on écrit de travers.
 *
 * ⚠️ CONFORT D'AFFICHAGE, comme tout `session.sections` : ces deux fonctions
 * décident de ce qu'on PROPOSE, jamais de ce qu'on autorise. Chaque route du
 * moteur revérifie pour son compte, et refuse.
 */
import type { DroitSection, RhAcces } from './types';

type Sections = Record<string, DroitSection> | undefined;

/**
 * Peut-on lire le planning du laboratoire — la semaine comme le mois ?
 *
 * Deux portes, et une seule suffit : le rôle `rh` (qui ouvre toute la
 * rubrique), ou la section `planning` en lecture (qui n'ouvre que celle-ci).
 *
 * ⚠️ Le repli est `false` et non `true`. Une session pas encore chargée rend
 * `rhAcces = null` et `sections = undefined` : afficher la grille « au cas
 * où » montrerait un instant le planning à qui n'y a pas droit. L'écran qui
 * appelle doit distinguer « pas encore su » de « refusé » par son propre
 * témoin de chargement — ce n'est pas à cette fonction de le deviner.
 */
export function peutLirePlanning(rhAcces: RhAcces | null | undefined, sections: Sections): boolean {
  return (rhAcces?.acces ?? false) || (sections?.planning?.voir ?? false);
}

/**
 * Le planning, ET RIEN D'AUTRE de la rubrique RH.
 *
 * C'est le cas du technicien : il lit le planning, mais `/ged/rh/personnel`,
 * `/ged/rh/contrats` et `/ged/rh/habilitations` lui restent fermées — elles
 * portent matricule, RIB et dates de sortie, qui ne regardent pas un planning.
 *
 * ⚠️ Distinct de `peutLirePlanning`, et pas seulement par politesse : c'est lui
 * qui dit à `loadRh` quelles listes NE PAS demander. Les confondre ferait
 * partir six requêtes vouées au 403 à chaque ouverture de l'écran.
 */
export function planningSeul(rhAcces: RhAcces | null | undefined, sections: Sections): boolean {
  return !(rhAcces?.acces ?? false) && (sections?.planning?.voir ?? false);
}

/**
 * Le prévisionnel du planning — réservé au rôle `rh` (RQ, 03/10/2026). La
 * section `planningprev` porte la condition de rôle côté serveur : ici, on ne
 * relit que sa réponse. Repli `false`, comme ci-dessus.
 */
export function peutVoirPrevisionnel(sections: Sections): boolean {
  return sections?.planningprev?.voir ?? false;
}

/** Le commentaire du mois, au-dessus du planning — même réserve (section `planningcom`). */
export function peutVoirCommentairePlanning(sections: Sections): boolean {
  return sections?.planningcom?.voir ?? false;
}

export function peutEcrireCommentairePlanning(sections: Sections): boolean {
  return sections?.planningcom?.modifier ?? false;
}
